/**
 * Mobile Menu Handler
 * Manages the mobile hamburger menu drawer functionality
 */
// Initialize logger for this module
const logger = window.Logger ? window.Logger.create('mobile-menu') : {
  debug: () => {},
  info: () => {},
  warn: () => {},
  error: console.error.bind(console)
};

class MobileMenu {
  constructor() {
    this.toggle = document.getElementById('mobileMenuToggle');
    this.closeBtn = document.getElementById('mobileMenuClose');
    this.overlay = document.getElementById('mobileMenuOverlay');
    this.drawer = document.getElementById('mobileMenuDrawer');
    this.body = document.body;
    this.isOpen = false;
    
    // Store original body overflow style to restore later
    this.originalBodyOverflow = '';
    
    this.init();
  }
  
  init() {
    if (!this.toggle || !this.overlay) {
      // The Console shell navigates by rail and tab bar; it has no drawer.
      if (document.querySelector('.app[data-site-shell="1"]')) {
        return;
      }
      logger.warn('[MobileMenu] Required elements not found');
      return;
    }
    
    // Check if we're on mobile and sidebar exists - if so, hamburger opens sidebar instead of drawer
    this.isMobile = () => window.innerWidth <= 768;
    this.hasSidebar = () => !!document.getElementById('accountSidebar');
    
    // Event listeners
    this.toggle.addEventListener('click', () => {
      if (this.isMobile() && this.hasSidebar()) {
        this.openSidebar();
      } else {
        this.open();
      }
    });
    
    this.closeBtn?.addEventListener('click', () => this.close());
    
    // Overlay click: close sidebar if open, or close drawer
    this.overlay.addEventListener('click', () => {
      if (this.isMobile() && this.hasSidebar() && document.documentElement.classList.contains('account-sidebar-expanded')) {
        this.closeSidebar();
      } else {
        this.close();
      }
    });
    
    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
    
    // Handle Add Item button in mobile menu
    const mobileAddItemBtn = document.getElementById('mobileAddItemBtn');
    if (mobileAddItemBtn) {
      mobileAddItemBtn.addEventListener('click', () => {
        // Check if global function exists (from base.html)
        if (typeof openAddItemModalGlobal === 'function') {
          openAddItemModalGlobal();
        } else {
          // Fallback: try to open modal directly (for trusted-finder.html)
          const addItemModal = document.getElementById('addItemModal');
          if (addItemModal) {
            addItemModal.classList.remove('hidden');
            addItemModal.style.display = 'block';
          }
        }
        this.close();
      });
    }
    
    // Close drawer when clicking on nav links
    const mobileNavLinks = this.drawer.querySelectorAll('.mobile-menu-nav .nav-link');
    mobileNavLinks.forEach(link => {
      link.addEventListener('click', () => {
        // Small delay to allow navigation to start
        setTimeout(() => this.close(), 100);
      });
    });
    
    // Prevent body scroll when drawer is open
    this.originalBodyOverflow = window.getComputedStyle(this.body).overflow;
  }
  
  open() {
    if (this.isOpen) return;
    
    this.isOpen = true;
    this.drawer.classList.add('is-open');
    this.overlay.classList.add('is-open');
    this.toggle.setAttribute('aria-expanded', 'true');
    this.body.classList.add('mobile-menu-open');
    
    // Lock body scroll
    this.body.style.overflow = 'hidden';
    
    // Focus management: focus the close button for accessibility
    this.closeBtn?.focus();
    
    // Prevent account sidebar from interfering (if present)
    const accountSidebar = document.querySelector('.account-sidebar');
    if (accountSidebar) {
      accountSidebar.style.zIndex = '999';
    }
  }
  
  close() {
    if (!this.isOpen) return;
    
    this.isOpen = false;
    this.drawer.classList.remove('is-open');
    this.overlay.classList.remove('is-open');
    this.toggle.setAttribute('aria-expanded', 'false');
    this.body.classList.remove('mobile-menu-open');
    
    // Restore body scroll
    this.body.style.overflow = this.originalBodyOverflow || '';
    
    // Return focus to toggle button
    this.toggle.focus();
    
    // Restore account sidebar z-index
    const accountSidebar = document.querySelector('.account-sidebar');
    if (accountSidebar) {
      accountSidebar.style.zIndex = '';
    }
  }
  
  toggleMenu() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }
  
  /**
   * Open account sidebar on mobile (instead of drawer)
   */
  openSidebar() {
    const sidebar = document.getElementById('accountSidebar');
    if (!sidebar) return;
    
    // Add expanded class to html element
    document.documentElement.classList.add('account-sidebar-expanded');
    
    // Show backdrop (reuse mobile-menu-overlay)
    this.overlay.classList.add('is-open');
    
    // Lock body scroll
    this.body.style.overflow = 'hidden';
    this.body.classList.add('mobile-menu-open');
    
    // Update aria-expanded on hamburger
    this.toggle.setAttribute('aria-expanded', 'true');
    
    // Focus sidebar toggle button for accessibility
    const sidebarToggle = document.getElementById('account-sidebar-toggle');
    sidebarToggle?.focus();
  }
  
  /**
   * Close account sidebar on mobile
   */
  closeSidebar() {
    // Remove expanded class
    document.documentElement.classList.remove('account-sidebar-expanded');
    
    // Hide backdrop
    this.overlay.classList.remove('is-open');
    
    // Restore body scroll
    this.body.style.overflow = this.originalBodyOverflow || '';
    this.body.classList.remove('mobile-menu-open');
    
    // Update aria-expanded on hamburger
    this.toggle.setAttribute('aria-expanded', 'false');
    
    // Return focus to hamburger
    this.toggle.focus();
    
    // Optionally clear sidebar state for mobile (don't persist mobile open state)
    if (this.isMobile()) {
      localStorage.removeItem('accountSidebarState');
    }
  }
}

// Global function for closing menu (called from onclick handlers in HTML)
window.closeMobileMenu = function() {
  if (window.mobileMenuInstance) {
    window.mobileMenuInstance.close();
  }
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.mobileMenuInstance = new MobileMenu();
  });
} else {
  window.mobileMenuInstance = new MobileMenu();
}

// Export for module systems
export default MobileMenu;
